import { type Entry, getMomentTz, useToken } from "@dexteel/mesf-core";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { INITIAL_VALUES_MODAL } from "../models/ModalInitialValue";
import type { ModalWorkOrder } from "../models/ModalWorkOrder";
import { EditWorkOrderModal } from "./modal-edit-work-order";

const moment = getMomentTz();

// The entry panel unmounts for a moment while a new entry is saved and the page
// switches to view mode, so this cannot live in component state or it is lost.
// Time bounded so a save that never lands does not open a modal much later.
let workOrderRequestedAt = 0;
const REQUEST_WINDOW_MS = 15000;

type Props = {
	entry: Entry;
	isNewEntry: boolean;
	// Resolves false when the entry was not saved (invalid form, cancelled
	// confirmation, failed save) so the button can leave the loading state
	requestSave: () => Promise<boolean>;
	onClosed?: () => void;
};

// Rendered in the local Logbook entry viewer footer
export const CreateWorkOrderFromLogbookEntry = ({
	entry,
	isNewEntry,
	requestSave,
	onClosed,
}: Props) => {
	const { getUserName } = useToken();
	const queryClient = useQueryClient();
	const [prefilledWorkOrder, setPrefilledWorkOrder] =
		useState<ModalWorkOrder | null>(null);
	// survives the brief entry-panel unmount via the module-scoped flag
	const [isRequesting, setIsRequesting] = useState(
		() =>
			workOrderRequestedAt !== 0 &&
			Date.now() - workOrderRequestedAt <= REQUEST_WINDOW_MS,
	);

	// Only a brand new entry needs saving. An entry that already exists is never
	// touched: opening a work order must not modify the logbook.
	const isEntryPersisted = !isNewEntry && !!entry.EntryId;

	const openModal = useCallback(() => {
		const openTimestamp = new Date();
		// Name and date, the columns actually visible in the logbook grid
		const entryDate = entry.CreateTimestamp
			? ` (${moment(entry.CreateTimestamp).format("MM/DD/YYYY")})`
			: "";
		const origin = `Created from Logbook entry "${entry.Title}"${entryDate}`;
		setPrefilledWorkOrder({
			...INITIAL_VALUES_MODAL,
			SourceEntryId: entry.EntryId ?? null,
			AssetId: entry.AssetId,
			Title: entry.Title,
			// Same "user - timestamp - text" convention the modal uses to append
			// comments, with the entry text on its own lines below the origin
			Comments: `${getUserName()} - ${moment(openTimestamp).format(
				"MM/DD/YYYY HH:mm",
			)} - ${origin}\n${entry.Content ?? ""}`,
			TypeCode: "RM",
			WorkOrderStatusCode: "O",
			WorkOrderOpenedBy: getUserName(),
			CraftId: 1,
			OpenTimestamp: openTimestamp,
		});
	}, [entry, getUserName]);

	const cancelRequest = useCallback(() => {
		workOrderRequestedAt = 0;
		setIsRequesting(false);
	}, []);

	const handleClick = async () => {
		if (isEntryPersisted) {
			openModal();
			return;
		}
		// New entry: save first, the modal opens once it comes back through props
		setIsRequesting(true);
		workOrderRequestedAt = Date.now();
		if (!(await requestSave())) cancelRequest();
	};

	// Safety net for any save that never reports back: never load forever
	useEffect(() => {
		if (!isRequesting || !workOrderRequestedAt) return;
		const elapsed = Date.now() - workOrderRequestedAt;
		const timer = setTimeout(
			cancelRequest,
			Math.max(REQUEST_WINDOW_MS - elapsed, 0),
		);
		return () => clearTimeout(timer);
	}, [isRequesting, cancelRequest]);

	useEffect(() => {
		if (!isEntryPersisted || !workOrderRequestedAt) return;
		const expired = Date.now() - workOrderRequestedAt > REQUEST_WINDOW_MS;
		workOrderRequestedAt = 0;
		if (!expired) openModal();
		setIsRequesting(false);
	}, [isEntryPersisted, openModal]);

	// No success toast here, the work order modal already shows its own on save
	const handleHide = () => {
		setPrefilledWorkOrder(null);
		setIsRequesting(false);
		queryClient.invalidateQueries(["entryWorkOrderCounts"]);
		onClosed?.();
	};

	return (
		<>
			<Button
				fullWidth
				variant="contained"
				color="primary"
				onClick={handleClick}
				disabled={isRequesting}
				startIcon={isRequesting ? <CircularProgress size="1rem" /> : undefined}
			>
				{isRequesting ? "Creating…" : "Create Work Order"}
			</Button>
			{/* The modal has its own form, and React bubbles events through the
			    component tree even across a portal. Without this the work order
			    submit would reach the logbook form and save the entry too. */}
			<div onSubmit={(event) => event.stopPropagation()}>
				<EditWorkOrderModal
					show={prefilledWorkOrder !== null}
					data={prefilledWorkOrder}
					initialNewMode
					onHide={handleHide}
				/>
			</div>
		</>
	);
};
